/**
 * Página de Aviso Legal - Estilo Awwwards
 */

import React, { useRef, useEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import ImmersiveLayout from '../components/layout/ImmersiveLayout';
import { AnimatedSection } from '../components/animations';

gsap.registerPlugin(ScrollTrigger);

/**
 * Página de Aviso Legal con estilo Awwwards
 * @returns {JSX.Element} Página de aviso legal inmersiva
 */
const LegalPage = () => {
  const heroRef = useRef(null);

  useEffect(() => {
    const hero = heroRef.current;
    if (!hero) return;

    const tl = gsap.timeline();
    tl.fromTo(hero.querySelector('.hero-title'),
      { y: 50, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.8, ease: 'power3.out' }
    );

    return () => tl.kill();
  }, []);

  return (
    <ImmersiveLayout>
      {/* Hero Section */}
      <section 
        ref={heroRef}
        className="relative min-h-[60vh] flex items-end pb-24 md:pb-32 overflow-hidden"
      >
        {/* Fondo negro base */}
        <div className="absolute inset-0 bg-intercasa-dark" />
        
        {/* Imagen en patrón repetido (tile) */}
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: 'url(/images/banners/banner-negro.webp)',
            backgroundSize: '350px auto',
            backgroundRepeat: 'repeat',
            backgroundPosition: 'center',
          }}
        />
        
        {/* Gradiente para mejor legibilidad */}
        <div className="absolute inset-0 bg-gradient-to-r from-intercasa-dark/90 via-intercasa-dark/70 to-intercasa-dark/90" />

        {/* Contenido del hero */}
        <div className="relative z-10 container mx-auto px-4 md:px-8">
          <div className="max-w-4xl">
            <h1 className="hero-title font-baskerville font-bold text-white text-5xl md:text-6xl lg:text-7xl xl:text-8xl leading-[0.95] mb-8 drop-shadow-lg">
              Aviso <span className="text-intercasa-yellow">Legal</span>
            </h1>
            <p className="text-lg md:text-xl text-white/60 max-w-2xl font-light">
              Información legal sobre el uso de nuestro sitio web
            </p>
          </div>
        </div>
      </section>

      {/* Contenido */}
      <section className="bg-white py-24 md:py-32">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-4xl mx-auto">
            <AnimatedSection animation="fadeUp">
              <div className="mb-16">
                <h2 className="font-baskerville text-3xl md:text-4xl font-bold text-intercasa-dark mb-6">
                  Información General
                </h2>
                <p className="text-intercasa-gray text-lg leading-relaxed mb-6">
                  En cumplimiento del artículo 10 de la Ley 34/2002, de 11 de julio, de Servicios de la Sociedad de la Información y Comercio Electrónico, se informa de los datos identificativos del titular del sitio web:
                </p>
                <div className="bg-intercasa-bone p-8 rounded-2xl border-l-4 border-intercasa-red">
                  <p className="text-intercasa-dark mb-3 text-lg"><strong>Titular:</strong> Intercasa S.A.</p>
                  <p className="text-intercasa-dark mb-3 text-lg"><strong>CIF:</strong> A-35.002.559</p>
                  <p className="text-intercasa-dark mb-3 text-lg"><strong>Domicilio:</strong> Canarias, España</p>
                  <p className="text-intercasa-dark text-lg"><strong>Email:</strong> info@intercasacanarias.es</p>
                </div>
              </div>
            </AnimatedSection>

            <AnimatedSection animation="fadeUp" delay={0.1}>
              <div className="mb-16">
                <h2 className="font-baskerville text-3xl md:text-4xl font-bold text-intercasa-dark mb-6">
                  Objeto
                </h2>
                <p className="text-intercasa-gray text-lg leading-relaxed">
                  El presente aviso legal regula el uso del sitio web www.intercasacanarias.es, del que es titular Intercasa S.A. La navegación por el sitio web atribuye la condición de usuario del mismo e implica la aceptación plena de todas las disposiciones incluidas en este aviso legal.
                </p>
              </div>
            </AnimatedSection>

            <AnimatedSection animation="fadeUp" delay={0.2}>
              <div className="mb-16">
                <h2 className="font-baskerville text-3xl md:text-4xl font-bold text-intercasa-dark mb-6">
                  Propiedad Intelectual e Industrial
                </h2>
                <p className="text-intercasa-gray text-lg leading-relaxed">
                  Todos los contenidos del sitio web, incluyendo, a título enunciativo pero no limitativo, textos, fotografías, gráficos, imágenes, iconos, tecnología, software, links y demás contenidos audiovisuales o sonoros, así como su diseño gráfico y códigos fuente, son propiedad intelectual de Intercasa S.A., sin que puedan entenderse cedidos al usuario ninguno de los derechos de explotación reconocidos por la normativa vigente en materia de propiedad intelectual sobre los mismos.
                </p>
              </div>
            </AnimatedSection>

            <AnimatedSection animation="fadeUp" delay={0.3}>
              <div className="mb-16">
                <h2 className="font-baskerville text-3xl md:text-4xl font-bold text-intercasa-dark mb-6">
                  Responsabilidad
                </h2>
                <p className="text-intercasa-gray text-lg leading-relaxed">
                  Intercasa S.A. no se hace responsable de los daños y perjuicios que pudieran derivarse de la utilización de la información del sitio web o de la contenida en las redes sociales de Intercasa S.A.
                </p>
              </div>
            </AnimatedSection>

            <AnimatedSection animation="fadeUp" delay={0.4}>
              <div>
                <h2 className="font-baskerville text-3xl md:text-4xl font-bold text-intercasa-dark mb-6">
                  Legislación Aplicable
                </h2>
                <p className="text-intercasa-gray text-lg leading-relaxed">
                  El presente aviso legal se rige por la legislación española vigente. Para la resolución de cualquier controversia las partes se someten a los Juzgados y Tribunales del domicilio del usuario.
                </p>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>
    </ImmersiveLayout>
  );
};

export default LegalPage;
